import { useEffect, useRef } from 'react';

const HOURS_AM = Array.from({ length: 12 }, (_, i) => i);
const HOURS_PM = Array.from({ length: 12 }, (_, i) => i + 12);
const MINUTES = Array.from({ length: 12 }, (_, i) => i * 5);

interface Props {
  value: Date;
  onChange: (date: Date) => void;
}

export function TimeColumns({ value, onChange }: Props) {
  const hour = value.getHours();
  const minute = value.getMinutes();

  return (
    <div className="flex divide-x divide-gray-4 th-highcontrast:divide-gray-6 th-dark:divide-gray-8">
      <TimeColumn
        values={HOURS_AM}
        selected={hour}
        format={(h) => String(h).padStart(2, '0')}
        onSelect={(h) => onChange(withHour(value, h))}
      />
      <TimeColumn
        values={HOURS_PM}
        selected={hour}
        format={(h) => String(h).padStart(2, '0')}
        onSelect={(h) => onChange(withHour(value, h))}
      />
      <TimeColumn
        values={MINUTES}
        selected={roundToNearest(minute, 5)}
        format={(m) => String(m).padStart(2, '0')}
        onSelect={(m) => onChange(withMinute(value, m))}
      />
    </div>
  );
}

function TimeColumn<T extends string | number>({
  values,
  selected,
  format,
  onSelect,
}: {
  values: T[];
  selected: T;
  format: (value: T) => string;
  onSelect: (value: T) => void;
}) {
  const selectedRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    selectedRef.current?.scrollIntoView({ block: 'center' });
  }, []);

  return (
    <div className="w-16 bg-white th-highcontrast:bg-black th-dark:bg-gray-11">
      {values.map((v) => {
        const isSelected = v === selected;
        return (
          <button
            key={v}
            ref={isSelected ? selectedRef : undefined}
            type="button"
            onClick={() => onSelect(v)}
            className={
              isSelected
                ? 'block w-full bg-blue-3 py-0.5 text-center text-xs font-medium text-blue-9 th-highcontrast:bg-blue-9 th-highcontrast:text-blue-3 th-dark:bg-blue-9 th-dark:text-blue-3'
                : 'block w-full bg-white py-0.5 text-center text-xs text-gray-8 hover:bg-gray-3 th-highcontrast:bg-black th-highcontrast:text-white th-highcontrast:hover:bg-gray-9 th-dark:bg-gray-11 th-dark:text-gray-5 th-dark:hover:bg-gray-9'
            }
          >
            {format(v)}
          </button>
        );
      })}
    </div>
  );
}

function withHour(date: Date, hour24: number) {
  const next = new Date(date);
  next.setHours(hour24);
  return next;
}

function withMinute(date: Date, minute: number) {
  const next = new Date(date);
  next.setMinutes(minute);
  return next;
}

function roundToNearest(value: number, step: number) {
  return Math.round(value / step) * step;
}
